import React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import cn from './cn';

/**
 * Radix Switch, replacing `react-switch`.
 *
 * Four screens used `react-switch`, each passing the same four styling props by
 * copy-paste (`onColor`, `onHandleColor`, `boxShadow`, `activeBoxShadow`) plus a height,
 * width and handle diameter. That is a component styled at its call sites, which is the
 * thing this layer exists to stop.
 *
 * The props are kept in the shape those call sites use: `checked` and `onChange` taking
 * the boolean directly, rather than Radix's `onCheckedChange`.
 *
 * Redesign iteration 1 (2026-09-29) gives every switch in the CMS one look, and this is the
 * React half of it; `store/rootComponents/themeControls.js` draws MUI's the same way. The
 * track is 36 by 20 with a white 16px knob inset 2px, which is what the padding is for.
 * It is brand when on, `--switch-off` when off (3.5:1 against white, where the old pale grey
 * was 1.6:1), and the pale grey when disabled, whichever side the knob is on. The knob's side
 * says the state by itself and its small shadow keeps it apart from the track, so neither
 * state depends on telling two colours apart. Hover changes nothing but the cursor.
 *
 * Both track colours are gated on `enabled:`. Tailwind emits `data-*` variants after
 * `disabled:`, so at equal specificity a checked switch stayed brand when it was disabled;
 * the extra pseudo-class is what lets the disabled grey win. Disabled no longer halves the
 * opacity either, because an opacity of a token is a new colour.
 *
 * The focus ring is an outline 2px outside the track, as on every other control, rather
 * than the ring utilities' box-shadow, whose offset is painted white and would show as a
 * halo on the grey panels some switches sit in.
 */
const Switch = React.forwardRef(
  ({ checked = false, onChange, disabled = false, className, id, ...props }, ref) => (
    <SwitchPrimitive.Root
      ref={ref}
      id={id}
      checked={checked}
      disabled={disabled}
      onCheckedChange={(next) => onChange && onChange(next)}
      className={cn(
        'tw-relative tw-inline-flex tw-h-switch tw-w-switch tw-shrink-0 tw-items-center',
        'tw-rounded-pill tw-border-0 tw-p-0.5',
        'tw-cursor-pointer tw-transition-colors tw-duration-150',
        'tw-bg-neutral-300 enabled:data-[state=checked]:tw-bg-brand',
        'enabled:data-[state=unchecked]:tw-bg-switch-off',
        'disabled:tw-cursor-not-allowed',
        'focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2',
        'focus-visible:tw-outline-brand-focus',
        className
      )}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'tw-block tw-h-knob tw-w-knob tw-rounded-pill tw-bg-white tw-shadow-sm',
          'tw-transition-transform tw-duration-150',
          'data-[state=checked]:tw-translate-x-4 data-[state=unchecked]:tw-translate-x-0'
        )}
      />
    </SwitchPrimitive.Root>
  )
);
Switch.displayName = 'Switch';

export default Switch;
